import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable Set-TDS dialog for Reimbursements and Advances.
 * kind: "reimbursement" | "advance"
 * item: full doc — needs id, gross_amount/amount, payee_id, payee_name, purpose/particulars
 * onSaved: called after successful set-tds so the parent can reload.
 */
export default function TdsPanel({ open, onOpenChange, kind, item, onSaved }) {
  const [tds, setTds] = useState([]);
  const [sectionId, setSectionId] = useState(item?.tds_section_id ?? item?.suggested_tds_section_id ?? "");
  const [rate, setRate] = useState(item?.tds_rate ?? item?.suggested_tds_rate ?? 0);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestion, setSuggestion] = useState(null);

  const gross = kind === "reimbursement" ? (item?.gross_amount || 0) : (item?.amount || 0);
  const tdsAmt = Math.round(gross * (parseFloat(rate) || 0)) / 100;
  const payable = Math.round((gross - tdsAmt) * 100) / 100;

  useEffect(() => {
    if (!open) return;
    api.get("/masters/tds-sections").then(r => setTds(r.data)).catch(() => setTds([]));
    setSectionId(item?.tds_section_id ?? item?.suggested_tds_section_id ?? "");
    setRate(item?.tds_rate ?? item?.suggested_tds_rate ?? 0);
    setReason(item?.tds_reason || "");
    setSuggestion(null);
  }, [open, item]);

  const suggest = async () => {
    setSuggesting(true);
    try {
      const invoiceDesc = kind === "reimbursement"
        ? (item?.particulars?.map(p => p.description).filter(Boolean).join(" · ") || item?.purpose || "")
        : (item?.purpose || item?.description || "");
      const r = await api.post("/ai/suggest-tds", {
        payee_id: item?.payee_id || null,
        payee_name: item?.payee_name || null,
        invoice_description: invoiceDesc,
        gross_amount: gross,
        fy_to_date_paid: 0,
      });
      setSuggestion(r.data);
      // Auto-pre-fill dropdown by matching section text
      const match = tds.find(t => t.section && r.data.section &&
                                   t.section.toUpperCase().includes(r.data.section.toUpperCase()));
      if (match) setSectionId(match.id);
      setRate(r.data.rate);
      toast.success(`AI suggests ${r.data.section} @ ${r.data.rate}%`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Suggestion unavailable");
    } finally { setSuggesting(false); }
  };

  const acceptAndSet = async () => {
    if (!suggestion) return;
    setRate(suggestion.rate);
    setReason(suggestion.reason || "");
    await save({ tds_section_id: sectionId || null, tds_rate: suggestion.rate, reason: suggestion.reason || "" });
  };

  const save = async (override) => {
    const body = override || { tds_section_id: sectionId || null, tds_rate: parseFloat(rate) || 0, reason };
    if ((body.tds_rate === 0 || body.tds_rate === 0.0) && !body.reason.trim()) {
      toast.error("Zero TDS needs a short reason (e.g. below threshold / exempt)");
      return;
    }
    setSaving(true);
    try {
      const path = kind === "reimbursement" ? `/reimbursements/${item.id}/set-tds` : `/advances/${item.id}/set-tds`;
      await api.post(path, body);
      toast.success(`TDS set — payable ₹${payable.toLocaleString()}`);
      onSaved && onSaved();
      onOpenChange(false);
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="tds-panel" className="max-w-lg">
        <DialogHeader><DialogTitle>Set TDS — {kind === "reimbursement" ? item?.rmb_id : item?.advance_id}</DialogTitle></DialogHeader>
        <div className="text-xs text-slate-500 -mt-2">
          Payee: <b>{item?.payee_name || item?.requester_name}</b> · Gross ₹{gross.toLocaleString()}
        </div>

        {(item?.suggested_tds_section_id || item?.suggested_tds_rate) && (
          <div className="mt-2 text-[11px] p-2 rounded bg-slate-50 border">
            Requester suggested: {item?.suggested_tds_rate || 0}%
          </div>
        )}

        <div className="mt-2">
          <Button variant="outline" size="sm" onClick={suggest} disabled={suggesting}
                  className="border-indigo-300 text-indigo-700 hover:bg-indigo-50" data-testid="ai-suggest-tds-btn">
            {suggesting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
            {suggesting ? "Asking Claude…" : "Suggest with AI"}
          </Button>
          {suggestion && (
            <div className="mt-2 p-3 rounded border border-indigo-200 bg-indigo-50 text-xs" data-testid="ai-suggestion-card">
              <div className="font-semibold">AI: {suggestion.section} @ {suggestion.rate}%</div>
              <div className="text-slate-700 mt-1">{suggestion.reason}</div>
              {suggestion.threshold_note && <div className="text-slate-500 text-[10px] mt-1">{suggestion.threshold_note}</div>}
              <Button size="sm" className="mt-2 h-7 bg-indigo-600 hover:bg-indigo-700 text-white" onClick={acceptAndSet} disabled={saving} data-testid="accept-ai-tds-btn">
                <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Accept & Set TDS
              </Button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <Label>Section</Label>
            <Select value={sectionId} onValueChange={(v) => { const t = tds.find(x => x.id === v); setSectionId(v); if (t?.default_rate != null) setRate(t.default_rate); }}>
              <SelectTrigger data-testid="tds-section-select"><SelectValue placeholder="Select section" /></SelectTrigger>
              <SelectContent>
                {tds.map(t => <SelectItem key={t.id} value={t.id}>{t.section} ({t.default_rate}%)</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Rate %</Label>
            <Input type="number" step="0.01" value={rate} onChange={e => setRate(e.target.value)} data-testid="tds-rate-input" />
          </div>
        </div>
        <div>
          <Label>Reason (required for 0%)</Label>
          <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Below threshold, exempt entity" data-testid="tds-reason-input" />
        </div>

        <div className="mt-2 p-3 rounded bg-slate-50 text-xs">
          Payable after TDS: <b>₹{payable.toLocaleString()}</b> · TDS deducted: ₹{tdsAmt.toLocaleString()}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-[#0F172A]" onClick={() => save()} disabled={saving} data-testid="set-tds-save-btn">
            {saving ? "Saving…" : "Set TDS & confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
